import { v4 as uuidv4 } from 'uuid';

/**
 * Usage:
 * const geo = new Geofence(payload)  // Instantiates
 * geo.onClick = ... // Handler for when geofence is clicked
 * geo.onDelete = ... // Handle for when geofence is deleted
 * geo.render(googleMap) // Renders the internal geoJson (__geom) onto the given map
 */
class Geofence {
  constructor(payload) {
    this.__mapPolygon = null;

    if (payload) {
      this.persisted = true;
      this.id = payload.id;
      this.name = payload.name;
      this.alertOnEnter = payload.alert_on_enter;
      this.alertOnExit = payload.alert_on_exit;

      // GeoJSON payload e.g {"type": "Polygon", "coordinates": [[[lng, lat], [lng, lat]]]}
      this.__geom = payload.geom;
    } else {
      this.persisted = false;
      this.id = uuidv4();
    }

    // To be registered later
    this.onClick = null;
    this.onDelete = null;
    this.onVertexDeleted = null;
  }

  /**
   * Renders the internal polygon representation (this.__geom) onto the given map.
   * @param {*} map
   */
  render(map) {
    if (this.__mapPolygon) {
      this.__mapPolygon.setMap(null);
    }

    const path = this.__geom ? this.__decodeGeoJsonToPath(this.__geom) : [];
    this.__mapPolygon = new window.google.maps.Polygon({
      paths: path || [],
      strokeColor: '#FF0000',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#FF0000',
      fillOpacity: 0.35,
      editable: this.__active,
    });

    this.__registerPolygonListeners(this.__mapPolygon);

    this.__mapPolygon.setMap(map);
  }

  addVertex(latLng) {
    const path = this.__mapPolygon.getPath() || [];
    path.push(latLng);
    this.__mapPolygon.setPath(path);
  }

  destroy() {
    this.__mapPolygon.setMap(null);
    this.__mapPolygon = null;
  }

  setActive(isActive) {
    this.__active = isActive;
    this.__mapPolygon.setEditable(isActive);
  }

  numVertices() {
    const path = this.__mapPolygon.getPath() || [];
    return path.length;
  }

  isValid() {
    // 3 vertices are required to be valid
    return this.numVertices() >= 3;
  }

  __registerPolygonListeners(mapPolygon) {
    window.google.maps.event.addListener(mapPolygon, 'click', (e) => {
      // If this geofence is 'active' and a vertex was clicked - delete the vertex
      if (e.vertex !== undefined && this.__active) {
        mapPolygon.getPath().removeAt(e.vertex);
        this.onVertexDeleted();
        if (mapPolygon.getPath().length === 0) {
          this.onDelete();
        }
        return;
      }

      if (this.onClick) {
        this.onClick();
      }
    });
  }

  __encodePathToGeoJson(path) {
    const coords = path.map(latlng => [latlng.lng(), latlng.lat()]);

    return {
      type: 'Polygon',
      coordinates: [
        coords,
      ],
    };
  }

  __decodeGeoJsonToPath(geoJson) {
    return geoJson.coordinates[0].map(e => ({ lng: e[0], lat: e[1] }));
  }

  /**
   * Returns a GeoJson definition of the currently rendered polygon (which
   * may have been manipulated by the user)
   */
  getLiveGeoJson() {
    return this.__encodePathToGeoJson(this.__mapPolygon.getPath().getArray());
  }
}

export default Geofence;
